// src/components/FolderSidebar.jsx
import React, { useState, useEffect } from 'react';
import { 
  Folder, 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  Users, 
  Briefcase, 
  Target, 
  Zap, 
  Star, 
  FileText, 
  Package,
  Plus,
  Edit2,
  Trash2,
  MoreVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { apiCall } from '../utils/api';

// Map icon names to components
const iconMap = {
  Folder,
  Chart: BarChart3,
  Trending: TrendingUp,
  Dollar: DollarSign,
  Users,
  Briefcase,
  Target,
  Zap,
  Star,
  Document: FileText,
  Package
};

export default function FolderSidebar({ selectedFolderId, onFolderSelect, onCreateFolder, onEditFolder }) {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    loadFolders();
  }, []);

  async function loadFolders() {
    setLoading(true);
    try {
      const res = await apiCall('/charts/folders/');
      if (res.ok) {
        const data = await res.json();
        setFolders(data.folders || []);
      }
    } catch (err) {
      console.error('Failed to load folders:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteFolder(folderId) {
    if (!window.confirm('Delete this folder? Charts in this folder will not be deleted.')) {
      return;
    }

    try {
      const res = await apiCall(`/charts/folders/${folderId}`, {
        method: 'DELETE'
      });

      if (res.status === 204 || res.ok) {
        setFolders(folders.filter(f => f.id !== folderId));
        if (selectedFolderId === folderId) {
          onFolderSelect(null); // Clear selection if deleted folder was selected
        }
      } else {
        alert('Failed to delete folder');
      }
    } catch (err) {
      console.error('Failed to delete folder:', err);
      alert('Failed to delete folder');
    }
  }

  const FolderIcon = ({ iconName }) => {
    const Icon = iconMap[iconName] || Folder;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="w-64 bg-[#1a1a1a] border-r border-[#2a2a2a] h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-[#2a2a2a]">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-white">Folders</h2>
          <button
            onClick={onCreateFolder}
            className="p-1.5 hover:bg-[#2a2a2a] rounded-lg transition-colors"
            title="Create Folder"
          >
            <Plus className="w-4 h-4 text-[#a0a0a0]" />
          </button>
        </div>
      </div>

      {/* All Charts Option */}
      <div className="p-2">
        <button
          onClick={() => onFolderSelect(null)}
          className={`
            w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left
            ${selectedFolderId === null 
              ? 'bg-blue-500/10 text-blue-500' 
              : 'text-[#a0a0a0] hover:bg-[#2a2a2a] hover:text-white'
            }
          `}
        >
          <div className={`
            w-8 h-8 rounded-lg flex items-center justify-center
            ${selectedFolderId === null ? 'bg-blue-500/20' : 'bg-[#2a2a2a]'}
          `}>
            <BarChart3 className="w-4 h-4" />
          </div>
          <span className="text-sm font-medium">All Charts</span>
        </button>
      </div>

      {/* Folders List */}
      <div className="flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : folders.length === 0 ? (
          <div className="text-center py-8">
            <Folder className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
            <p className="text-xs text-[#6a6a6a]">No folders yet</p>
          </div>
        ) : (
          <div className="space-y-1">
            {folders.map(folder => (
              <div
                key={folder.id}
                className="relative group"
              >
                <button
                  onClick={() => onFolderSelect(folder.id)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left
                    ${selectedFolderId === folder.id 
                      ? 'bg-blue-500/10 text-white' 
                      : 'text-[#a0a0a0] hover:bg-[#2a2a2a] hover:text-white'
                    }
                  `}
                >
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ 
                      backgroundColor: folder.color + '20',
                      color: folder.color
                    }}
                  >
                    <FolderIcon iconName={folder.icon} />
                  </div>
                  <span className="text-sm font-medium flex-1 truncate">
                    {folder.name}
                  </span>
                </button>

                {/* Folder Actions Dropdown */}
                <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveDropdown(activeDropdown === folder.id ? null : folder.id);
                    }}
                    className="p-1 hover:bg-[#3a3a3a] rounded transition-colors"
                  >
                    <MoreVertical className="w-3 h-3 text-[#a0a0a0]" />
                  </button>

                  {activeDropdown === folder.id && (
                    <>
                      {/* Backdrop */}
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setActiveDropdown(null)}
                      />

                      {/* Dropdown Menu */}
                      <div className="absolute right-0 mt-1 w-40 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg shadow-xl z-50">
                        <button
                          onClick={() => {
                            setActiveDropdown(null);
                            onEditFolder(folder);
                          }}
                          className="w-full px-3 py-2 text-left text-xs text-white hover:bg-[#2a2a2a] transition-colors flex items-center gap-2"
                        >
                          <Edit2 className="w-3 h-3" />
                          Edit Folder
                        </button>
                        <button
                          onClick={() => {
                            setActiveDropdown(null);
                            handleDeleteFolder(folder.id);
                          }}
                          className="w-full px-3 py-2 text-left text-xs text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-2"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}